import { ContentPublicRelayDto } from '@diritto/common/relay/dtos/content-relay.dtos';
import { Color } from '@diritto/common/style/color';
import { FontWeight, Typography } from '@diritto/common/style/typography';
import { numberComma } from '@diritto/common/utils/string';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { ProfileAvatar } from 'app-components/accounts/profiles/ProfileAvatar';
import { ZIndex } from 'constants/zIndex';
import { ArrowIcon } from 'icons/ArrowIcon';
import { ReplyBlankIcon } from 'icons/ReplyBlankIcon';
import { VisibilityIcon } from 'icons/VisibilityIcon';
import React, { useEffect, useState } from 'react';
import { useContentReaction } from 'state-hooks/contents/useContentReaction';
import { useContentReactionStore } from 'stores/contents/ContentReactionStore';
import { DotDivider } from 'ui-components/common/DotDivider';
import { Link } from 'ui-components/common/Link';
import { IconCount, IconCountType } from 'ui-components/contents/IconCount';

interface ContentDetailIconCountsProps {
  className?: string;
  content: ContentPublicRelayDto;
}

export const ContentDetailIconCounts = ({ className, content }: ContentDetailIconCountsProps) => {
  const { viewCount, favoriteCount, newEpisodeNotiCount, recommendCount, replyCount } = content.counts;
  const { store } = useContentReactionStore();
  const { favorite, noti } = useContentReaction(content.id);

  const [recentlyReadProfileNicknames, setRecentlyReadProfileNicknames] = useState<string[]>([]);
  useEffect(() => {
    setRecentlyReadProfileNicknames([...Array(viewCount > 3 ? 3 : viewCount)].map((_, it) => String(viewCount + it)));
  }, [viewCount]);

  useEffect(() => {
    store.updateContentFavoriteCount(content.id, favoriteCount);
    store.updateContentNotiCount(content.id, newEpisodeNotiCount);
  }, [content.id, favoriteCount, newEpisodeNotiCount, store]);

  return (
    <StyledContainer className={className}>
      <StyledAc>
        <div className="size-[14px]">
          <VisibilityIcon color={Color.Gray400} />
        </div>
        <span>{numberComma(viewCount)}명 보는 중</span>
        <StyledAvatarList>
          {recentlyReadProfileNicknames.map(it => (
            <StyledAvatarWrapper key={it} size={18}>
              <ProfileAvatar name={it} />
            </StyledAvatarWrapper>
          ))}
        </StyledAvatarList>
      </StyledAc>
      <StyledAc>
        <StyledLink href={`/contents/${content.id}/replies`}>
          <ReplyBlankIcon className="size-[14px]" color={Color.Gray400} />
          <span>{numberComma(replyCount)}개의 댓글</span>
          <ArrowIcon direction="right" className="size-[14px]" color={Color.Gray500} />
        </StyledLink>
      </StyledAc>
      <StyledIconContainer>
        <StyledIconCount
          type={IconCountType.Favorite}
          count={favorite.favoriteCount || favoriteCount}
          color={Color.Gray400}
        />
        <DotDivider />
        <StyledIconCount
          type={IconCountType.NewEpisodeNoti}
          count={noti.notiCount || newEpisodeNotiCount}
          color={Color.Gray400}
        />
        <DotDivider />
        <StyledIconCount type={IconCountType.Recommend} count={recommendCount} color={Color.Gray400} />
      </StyledIconContainer>
    </StyledContainer>
  );
};

const StyledContainer = styled.div`
  > * {
    margin-bottom: 5px;
  }
`;

const StyledLink = styled(Link)`
  display: flex;
  align-items: center;
  justify-content: center;

  > * {
    margin-right: 7px;
  }
`;

const StyledAc = styled.div`
  display: flex;
  flex-direction: row;
  color: ${Color.Gray600};
  ${Typography.XXXSmall};
  font-weight: ${FontWeight.Medium};
  align-items: center;

  > * {
    margin-right: 5px;
  }
`;

const StyledAvatarList = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;

  > * {
    margin-right: -4px;
    border-radius: 50%;
  }
`;

const StyledAvatarWrapper = styled.div(
  ({ size }: { size: number }) => css`
    width: ${size}px;
    height: ${size}px;
    z-index: ${ZIndex.ProfileImageBorder};

    &::before {
      content: '';
      position: absolute;
      border-radius: 50%;
      z-index: ${ZIndex.ProfileImageBorder};
      width: ${size}px;
      height: ${size}px;
      box-shadow: inset 0 0 0 1px rgba(0, 0, 0, 0.1);
    }

    > * {
      border-radius: 50%;
      width: ${size}px;
      height: ${size}px;
    }
  `,
);

const StyledIconContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 4px;
`;

const StyledIconCount = styled(IconCount)`
  & > p {
    color: ${Color.Gray600};
  }
`;
